import { notification, Modal } from 'antd';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import LinearProgress from '@mui/material/LinearProgress';
import {
  useLazyExportWorkspaceTasksQuery,
  useLazyExportProjectTasksQuery,
} from '../../../../services/tasksApi';
import { useEffect } from 'react';
import { convertFiltersToQueryString } from '../../../../redux/tasks/helpers';
import AllCustomFieldsForExport from './AllCustomFieldsForExport';

const fieldOptions = (isWorkspace) => [
  ...(isWorkspace
    ? [{ value: 'projects', label: 'Projects' }]
    : [
        { value: 'projectTitle', label: 'Project Name' },
        { value: 'sectionTitle', label: 'Section Name' },
      ]),
  { value: 'taskId', label: 'Task ID' },
  { value: 'taskTitle', label: 'Task Name' },
  { value: 'taskDescription', label: 'Task Description' },
  { value: 'status', label: 'Status' },
  { value: 'assignee', label: 'Assignee' },
  { value: 'dueDate', label: 'Due Date' },
  { value: 'priority', label: 'Priority' },
  { value: 'createdAt', label: 'Created Date' },
  { value: 'updatedAt', label: 'Last Modified Date' },
  { value: 'isCompleted', label: 'Completed' },
  { value: 'completedAt', label: 'Completed At' },
  { value: 'completedBy', label: 'Completed By' },
  { value: 'taskLink', label: 'Task Link' },
];

const ExportDataModal = ({
  open,
  toggle,
  projectId,
  workspaceId,
  isWorkspace,
  filters,
}) => {
  const [notificationApi, notificationContextHolder] = notification.useNotification();
  const [exportWorkspaceTasks] = useLazyExportWorkspaceTasksQuery();
  const [exportProjectTasks] = useLazyExportProjectTasksQuery();

  const options = fieldOptions(isWorkspace);

  const [selectedFields, setSelectedFields] = React.useState(options.map((f) => f.value));
  const [allCustomFieldsSelected, setAllCustomFieldsSelected] = React.useState(false);
  const [selectedCustomFields, setSelectedCustomFields] = React.useState([]);
  const [exportInProgress, setExportInProgress] = React.useState(false);

  const allSelected = selectedFields.length === options.length && allCustomFieldsSelected;
  const someSelected = selectedFields.length > 0 || selectedCustomFields.length > 0;

  const toggleField = (value) => {
    setSelectedFields((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedFields([]);
      setSelectedCustomFields([]);
      setAllCustomFieldsSelected(false);
    } else {
      setSelectedFields(options.map((f) => f.value));
      setAllCustomFieldsSelected(true);
    }
  };

  const handleExport = async () => {
    notificationApi.info({
      duration: false,
      key: 'export-tasks',
      placement: 'bottomRight',
      message: (
        <span className="text-sm font-semibold text-gray-800">
          <FormattedMessage id="TASKS_EXPORT_IN_PROGRESS_NOTIFICATION" defaultMessage="Preparing your export…" />
        </span>
      ),
      description: (
        <div className="flex flex-col gap-2 mt-1">
          <span className="text-xs text-gray-500">
            <FormattedMessage id="DO_NOT_CLOSE_OR_REFRESH" defaultMessage="Please do not close or refresh this tab." />
          </span>
          <LinearProgress />
        </div>
      ),
    });

    setExportInProgress(true);
    toggle();

    const filtersQuery = filters ? convertFiltersToQueryString(filters) : '';
    let queryString = selectedFields.length
      ? `${filtersQuery.length ? `${filtersQuery}&` : '?'}fields=${selectedFields.join(',')}${selectedCustomFields?.length ? `&customFields=${selectedCustomFields.join(',')}` : ''}`
      : filtersQuery;

    // Pass the viewer's timezone so exported dates match what the UI shows
    // (dates are stored as instants; formatting server-side in UTC read a day early).
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone) {
      queryString += `${queryString.includes('?') ? '&' : '?'}timezone=${encodeURIComponent(timezone)}`;
    }

    const blob = isWorkspace
      ? await exportWorkspaceTasks({ workspaceId, query: queryString }).unwrap()
      : await exportProjectTasks({ projectId, query: queryString }).unwrap();

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = isWorkspace ? 'workspace_tasks.csv' : 'project_tasks.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    setExportInProgress(false);
    notificationApi.destroy('export-tasks');
  };

  useEffect(() => {
    if (open) setSelectedFields([]);
  }, [open]);

  return (
    <>
      {notificationContextHolder}
      <Modal
        open={open}
        onCancel={toggle}
        footer={null}
        width={480}
        styles={{ body: { padding: 0 }, content: { borderRadius: 16, overflow: 'hidden', padding: 0 } }}
        closable={false}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              </div>
              <h2 className="text-base font-semibold text-gray-900">
                {isWorkspace ? 'Export Workspace Tasks' : 'Export Project Tasks'}
              </h2>
            </div>
            <p className="text-sm text-gray-400 ml-10">Choose which fields to include in your CSV file.</p>
          </div>
          <button
            type="button"
            onClick={toggle}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0 mt-0.5"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          {/* Select all row */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Fields</span>
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
              {allSelected ? 'Deselect all' : 'Select all'}
            </button>
          </div>

          {/* Field grid */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mb-4">
            {options.map((opt) => (
              <label
                key={opt.value}
                className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-gray-50 cursor-pointer group"
              >
                <div
                  className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border transition-colors ${
                    selectedFields.includes(opt.value)
                      ? 'bg-blue-600 border-blue-600'
                      : 'bg-white border-gray-300 group-hover:border-gray-400'
                  }`}
                  onClick={() => toggleField(opt.value)}
                >
                  {selectedFields.includes(opt.value) && (
                    <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="2,6 5,9 10,3"/>
                    </svg>
                  )}
                </div>
                <span
                  className="text-sm text-gray-700 select-none"
                  onClick={() => toggleField(opt.value)}
                >
                  {opt.label}
                </span>
              </label>
            ))}
          </div>

          {/* Custom fields */}
          {isWorkspace && (
            <div className="border-t border-gray-100 pt-4">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Custom Fields</p>
              <AllCustomFieldsForExport
                allCustomFieldsSelected={allCustomFieldsSelected}
                selectedCustomFields={selectedCustomFields}
                setAllCustomFieldsSelected={setAllCustomFieldsSelected}
                setSelectedCustomFields={setSelectedCustomFields}
                filters={filters}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
          <span className="text-xs text-gray-400">
            {selectedFields.length + selectedCustomFields.length} field{selectedFields.length + selectedCustomFields.length !== 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggle}
              className="h-8 px-4 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <FormattedMessage id="CANCEL" defaultMessage="Cancel" />
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={exportInProgress || !someSelected}
              className="h-8 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold text-white transition-colors flex items-center gap-1.5"
            >
              {exportInProgress ? (
                <>
                  <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                  <FormattedMessage id="EXPORTING" defaultMessage="Exporting…" />
                </>
              ) : (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  <FormattedMessage id="EXPORT" defaultMessage="Export" />
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ExportDataModal;
